import { spanishCrmMessages } from "../providers/commons/spanishCrmMessages";
import type {
  BusinessUnit,
  Contact,
  ContactType,
  LeadSource,
  Project,
} from "../types";
import { BUSINESS_UNITS, CONTACT_TYPES } from "../types";
import { splitSurnames } from "./contactModel";

/**
 * Almikia Epic EC2a: pure parsing rules of the contacts CSV import (types, origin, business unit,
 * BPO project). Every failure carries a Spanish message that the importer puts in the per-row
 * error, because the people loading the file read Spanish.
 */

/** Lower-cases and strips accents so "Aliado", "aliado" and "ALIADO" compare equal. */
const normalize = (text: string): string =>
  text.normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();

const typeLabels = spanishCrmMessages.resources.contacts.contact_types;

/** Every accepted spelling of a contact type: its English identifier or its Spanish label. */
const TYPE_BY_TEXT: ReadonlyMap<string, ContactType> = new Map(
  CONTACT_TYPES.flatMap((type): [string, ContactType][] => [
    [normalize(type), type],
    [normalize(typeLabels[type]), type],
  ]),
);

/** Placeholder apellido materno of a legacy contact whose surname cannot be split (reviewed by a person). */
export const MISSING_SECOND_SURNAME = "S/A";

/**
 * EC2a fields a legacy source with no types, second surname, origin or business unit (the JSON
 * import) must supply to pass the DB guard: the Aliado type, the split surname flagged for review
 * ('S/A' when the split is uncertain), the given origin and the Corretaje unit.
 */
export const legacyContactFields = (
  lastName: string,
  leadSourceId: LeadSource["id"],
): Pick<
  Contact,
  | "last_name"
  | "second_last_name"
  | "name_needs_review"
  | "contact_types"
  | "lead_source_id"
  | "business_unit"
> => {
  const split = splitSurnames(lastName);
  return {
    last_name: split.paterno ?? lastName,
    second_last_name: split.materno ?? MISSING_SECOND_SURNAME,
    name_needs_review: true,
    contact_types: ["ally"],
    lead_source_id: leadSourceId,
    business_unit: "corretaje",
  };
};

export type ParseResult<T> = { value: T } | { error: string };

/**
 * Parses the comma-separated `contact_types` cell (English identifiers or Spanish labels, e.g.
 * "colega, Proveedor") into distinct types. An empty cell yields no types (the mandatory rule
 * then rejects the row); an unknown name is an error naming it.
 */
export const parseContactTypes = (
  cell: string | undefined,
): ParseResult<ContactType[]> => {
  const names = (cell ?? "")
    .split(",")
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
  const types: ContactType[] = [];
  for (const name of names) {
    const type = TYPE_BY_TEXT.get(normalize(name));
    if (!type) return { error: `Tipo de contacto desconocido: "${name}"` };
    if (!types.includes(type)) types.push(type);
  }
  return { value: types };
};

/**
 * Resolves the `origin` cell against the lead source catalogue by name, case-insensitive. An
 * empty cell resolves to nothing (mandatory rule); an unknown name is an error.
 */
export const resolveOrigin = (
  cell: string | undefined,
  sources: readonly LeadSource[],
): ParseResult<LeadSource | undefined> => {
  if (cell === undefined) return { value: undefined };
  const source = sources.find(
    (candidate) => normalize(candidate.name) === normalize(cell),
  );
  return source
    ? { value: source }
    : { error: `Origen desconocido: "${cell}"` };
};

/** Resolves the `business_unit` cell ("corretaje" or "bpo", case-insensitive). */
export const resolveBusinessUnit = (
  cell: string | undefined,
): ParseResult<BusinessUnit | undefined> => {
  if (cell === undefined) return { value: undefined };
  const unit = BUSINESS_UNITS.find(
    (candidate) => candidate === normalize(cell),
  );
  return unit
    ? { value: unit }
    : { error: `Unidad de negocio desconocida: "${cell}"` };
};

/** Resolves the BPO `project` cell by code or name, case-insensitive. */
export const resolveProject = (
  cell: string | undefined,
  projects: readonly Project[],
): ParseResult<Project | undefined> => {
  if (cell === undefined) return { value: undefined };
  const project = projects.find(
    (candidate) =>
      normalize(candidate.code) === normalize(cell) ||
      normalize(candidate.name) === normalize(cell),
  );
  return project
    ? { value: project }
    : { error: `Proyecto desconocido: "${cell}"` };
};
